// Per-type methodology within one scope — today Scope 1's Stationary / Mobile / Refrigerants.
//
// `organization_questionnaire.methodology_map` is a free-form jsonb map and
// `PATCH /organization-questionnaires/{id}/methodology` takes any key, so each type is stored under its own
// key (`scope1.stationary`, …) with no backend change. The shared `scope1` key is still what the backend's
// ai-ingest commit appends its record to (AiIngestFacade.appendMethodology) and what older data holds, so:
//
//  - a type's tab shows its own key when set;
//  - otherwise it is SEEDED from the shared `scope1` value: the ai-ingest sections for that type's target
//    (`<!-- ai-ingest:target=SCOPE_1_STATIONARY -->…`), plus — for the FIRST type only — any text outside a
//    section (hand-typed, or written before per-type tabs existed), so it is not lost and not duplicated
//    across three tabs.
//
// TODO(backend): an ai-ingest commit still writes into the shared `scope1` key. Until the backend writes per
// type, the data mapping commit copies its new section into the type's own key afterwards (see
// aiDataMapping/components/methodologyMode.js), and a section committed by an older client shows up here via
// the seeding above.
//
// Its own file (not the utils barrel, which contains JSX) so it can carry a vitest.

export const SCOPE_SUB_TYPES = {
  scope1: [
    { key: "scope1.stationary", label: "Stationary", target: "SCOPE_1_STATIONARY" },
    { key: "scope1.mobile", label: "Mobile", target: "SCOPE_1_MOBILE" },
    { key: "scope1.refrigerant", label: "Refrigerants", target: "SCOPE_1_REFRIGERANT" },
  ],
};

/** The per-type key for an ai-ingest target, or null when the target's scope has no sub-types. */
export function subKeyForTarget(target) {
  for (const types of Object.values(SCOPE_SUB_TYPES)) {
    const match = types.find((type) => type.target === target);
    if (match) return match.key;
  }
  return null;
}

const SECTION_RE = /<!--\s*ai-ingest:target=([A-Za-z0-9_]+)\s*-->[\s\S]*?<!--\s*\/ai-ingest\s*-->/g;

const hasText = (html) => Boolean(html && html.replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").trim());

/**
 * What a type's tab shows: its own stored value if there is one, else what the shared scope value holds for
 * it (see the file header). `isFirst` gives the shared value's free text to the first type.
 */
export function methodologyForSubType(methodologyMap, scopeKey, type, isFirst) {
  const own = methodologyMap?.[type.key];
  if (own != null) return own;
  const shared = methodologyMap?.[scopeKey];
  if (!shared) return "";
  const parts = [];
  let match;
  SECTION_RE.lastIndex = 0;
  while ((match = SECTION_RE.exec(shared)) !== null) {
    if (match[1] === type.target) parts.push(match[0]);
  }
  if (isFirst) {
    const free = shared.replace(SECTION_RE, "").trim();
    if (hasText(free)) parts.unshift(free);
  }
  return parts.join("\n\n");
}

/** True when any of a scope's types has visible text — for the task's "has methodology" check. */
export function scopeHasSubTypeMethodology(methodologyMap, scopeKey) {
  const types = SCOPE_SUB_TYPES[scopeKey] || [];
  return types.some((type, index) => hasText(methodologyForSubType(methodologyMap, scopeKey, type, index === 0)));
}
